// Everything about one patient's medicines, in one place.
//
//   (A) Medicine list      — the ORDER BOOK: what this patient is on.
//   (B) Administration record — what has actually been given.
//
// (A) is new: the order book has been fetchable since the CPOE sprint and had
// no consumer at all, so "what is this patient on?" was a question the nurse
// dashboard could not answer. (B) is the former eMAR tab, moved here so the
// two finally sit together — a record of doses is not readable without the
// orders it records against.
import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Box, Button, Chip, Collapse, IconButton, LinearProgress, Paper, Stack, Typography,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";

import { NURSE_COLORS } from "../theme";
import { useNurseData } from "../NurseDataContext";
import {
  getPrescriptionsByAdmission, getNurseTasks, getRoster, getAdHocDoseForm,
  getMedicationReturnable, listOf,
} from "../api/nurseApi";
import { indexStock, stockFor } from "../wardStock";
import { todayStr } from "../dateNav";
import MarGrid from "../components/MarGrid";
import MedicationAdministrationSheet from "../components/MedicationAdministrationSheet";

// Order-book lifecycle (Prescription.status). Cancel is terminal and retires
// the medicine; the rest march forward towards the ward.
const ORDER_STATUS_COLOR = {
  Sent: "default",
  Acknowledged: "info",
  Dispensed: "warning",
  Received: "success",
  Cancel: "default",
};

const isCancelled = (row) => String(row?.status || "").toLowerCase().startsWith("cancel");

// "1-0-0-1" — the dose pattern a nurse writes on a chart, in round order.
// Absent on orders placed before per-round doses were captured, and on
// frequencies where rounds don't apply (as-needed, STAT, hourly cycles).
const SLOT_KEYS = ["morning", "afternoon", "evening", "night"];
function slotPattern(details) {
  const values = SLOT_KEYS.map((k) => Number(details?.[k]) || 0);
  return values.some((v) => v > 0) ? values.join("-") : "";
}

// The ordered regimen on one line: how much, how, how often, how long.
function regimenLine(row) {
  const d = row?.details || {};
  const duration = d.duration_days
    ? `${d.duration_days} day${d.duration_days === 1 ? "" : "s"}`
    : (d.duration || "");
  return [slotPattern(d), d.dose, d.route, d.frequency_code || d.frequency, duration]
    .filter(Boolean)
    .join(" · ");
}

// An as-needed medicine schedules no doses at all, so it can ONLY be given
// through Give now — without it, an acknowledged order sitting on the ward has
// no button anywhere in the app that administers it.
const AS_NEEDED = /^(sos|prn)$/i;
const isAsNeeded = (row) => AS_NEEDED.test(String(row?.details?.frequency_code || row?.details?.frequency || ""));

// ------------------------------------------------------------- (A) medicine list
function MedicineList({ rows, loading, stock, onGiveNow }) {
  const [open, setOpen] = useState(true);

  // Rows arrive newest-first, one per medicine. Medicines sharing an order_no
  // were ordered together and belong under one heading.
  const orders = useMemo(() => {
    const byOrder = new Map();
    rows.forEach((row) => {
      const key = row.order_no || `#${row.id}`;
      if (!byOrder.has(key)) byOrder.set(key, { orderNo: key, doctor: row.doctor_name, rows: [] });
      byOrder.get(key).rows.push(row);
    });
    return Array.from(byOrder.values());
  }, [rows]);

  return (
    <Box sx={{ mb: 3 }}>
      <Stack direction="row" sx={{ alignItems: "center", justifyContent: "space-between", mb: 1 }}>
        <Stack direction="row" spacing={1} sx={{ alignItems: "center" }}>
          <Typography variant="subtitle1">Medicine List</Typography>
          {!loading && <Chip size="small" variant="outlined" label={`${rows.length} medicine${rows.length === 1 ? "" : "s"}`} />}
        </Stack>
        <IconButton size="small" onClick={() => setOpen((v) => !v)} aria-label={open ? "Collapse" : "Expand"}>
          {open ? <ExpandLessIcon /> : <ExpandMoreIcon />}
        </IconButton>
      </Stack>

      <Collapse in={open}>
        {loading && <LinearProgress sx={{ mb: 1 }} />}
        {!loading && orders.length === 0 && (
          <Paper variant="outlined" sx={{ p: 3, textAlign: "center", borderRadius: 2, color: NURSE_COLORS.textSecondary }}>
            No medicines ordered for this patient.
          </Paper>
        )}
        <Stack spacing={1.5}>
          {orders.map((order) => (
            <Paper key={order.orderNo} variant="outlined" sx={{ borderRadius: 2, overflow: "hidden" }}>
              <Stack
                direction="row"
                sx={{ px: 2, py: 1, alignItems: "center", justifyContent: "space-between", bgcolor: NURSE_COLORS.pageBg }}
              >
                <Typography variant="caption" color="text.secondary">
                  {order.orderNo}{order.doctor ? ` · Dr. ${order.doctor}` : ""}
                </Typography>
              </Stack>
              <Box>
                {order.rows.map((row) => {
                  const cancelled = isCancelled(row);
                  return (
                    <Stack
                      key={row.id}
                      direction="row"
                      spacing={1}
                      sx={{
                        px: 2, py: 1.25, alignItems: "center",
                        borderTop: `1px solid ${NURSE_COLORS.border}`,
                        opacity: cancelled ? 0.55 : 1,
                      }}
                    >
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography
                          variant="body2"
                          sx={{ fontWeight: 600, textDecoration: cancelled ? "line-through" : "none" }}
                        >
                          {row.medicine_name || "—"}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {regimenLine(row) || "No regimen recorded"}
                        </Typography>
                      </Box>
                      {(() => {
                        const s = stockFor(stock, row.id);
                        // Only a real count earns a chip. "unknown" and
                        // "not-dispensed" say nothing a nurse can act on here,
                        // and a chip reading 0 for either would be a lie.
                        if (s.state !== "known") return null;
                        return (
                          <Chip
                            size="small"
                            variant="outlined"
                            color={s.onHand <= 0 ? "warning" : "default"}
                            label={`${s.onHand} left`}
                          />
                        );
                      })()}
                      <Chip
                        size="small"
                        label={row.status || "—"}
                        color={ORDER_STATUS_COLOR[row.status] || "default"}
                        variant={row.status === "Received" ? "filled" : "outlined"}
                      />
                      {!cancelled && (
                        <Button
                          size="small"
                          variant={isAsNeeded(row) ? "contained" : "text"}
                          onClick={() => onGiveNow(row)}
                        >
                          Give now
                        </Button>
                      )}
                    </Stack>
                  );
                })}
              </Box>
            </Paper>
          ))}
        </Stack>
      </Collapse>
    </Box>
  );
}

// ---------------------------------------------------------------------- tab
export default function MedicationsTab({ admissionId }) {
  const { currentNurseId, patients } = useNurseData();
  const [orders, setOrders] = useState([]);
  const [ordersLoading, setOrdersLoading] = useState(true);
  const [tasks, setTasks] = useState([]);
  const [tasksLoading, setTasksLoading] = useState(true);
  const [stock, setStock] = useState({});
  const [date, setDate] = useState(todayStr);
  const [giving, setGiving] = useState(null);
  // performed_by_id off a task is an opaque id the tasks endpoint never
  // resolves to a name — resolve it against the roster rather than showing it raw.
  const [nurseNameById, setNurseNameById] = useState({});

  const loadOrders = useCallback(() => {
    if (!admissionId) return;
    setOrdersLoading(true);
    getPrescriptionsByAdmission(admissionId)
      // Flat envelope: a 200 can still be "Failed", so check status before reading data.
      .then((res) => setOrders(res?.status === "Success" ? listOf(res) : []))
      .catch(() => setOrders([]))
      .finally(() => setOrdersLoading(false));
  }, [admissionId]);

  const loadTasks = useCallback(() => {
    if (!admissionId) return;
    setTasksLoading(true);
    getNurseTasks({ admission_id: admissionId })
      .then((r) => setTasks(listOf(r)))
      .catch(() => setTasks([]))
      .finally(() => setTasksLoading(false));
  }, [admissionId]);

  // What the ward still holds, per medicine. Derived server-side — see
  // wardStock.js for why this is never a stored balance.
  const loadStock = useCallback(() => {
    if (!admissionId) return;
    getMedicationReturnable(admissionId)
      // Flat envelope — a 200 can still be "Failed".
      .then((res) => setStock(indexStock(res?.status === "Success" ? listOf(res) : [])))
      // Stock is additive information. Losing it must degrade the tab to its
      // previous behaviour, never break the medicine list.
      .catch(() => setStock({}));
  }, [admissionId]);

  // Taken from the dashboard's own patient list rather than from a task: a
  // patient whose only order is as-needed has NO tasks at all, and that is
  // exactly the case Give now exists for.
  const patientInfo = useMemo(() => {
    const p = patients.find((x) => String(x.admissionId) === String(admissionId));
    return p ? { name: p.name, bed_code: p.bed } : null;
  }, [patients, admissionId]);

  // Give now: an ad-hoc dose has no task, so build the shape the sheet reads
  // from the order-book row, and ask the backend which form applies rather than
  // re-implementing that rule here.
  const giveNow = useCallback(async (row) => {
    let form = null;
    try {
      const res = await getAdHocDoseForm(row.id);
      if (res?.status === "Success") form = res.fulfillment_form;
    } catch {
      // Fall through with no form: the sheet still opens, and the backend
      // re-validates on submit, so a missing form fails loudly rather than
      // silently recording an incomplete dose.
    }
    setGiving({
      id: null,
      prescription_id: row.id,
      prescription_medicine_name: row.medicine_name,
      prescription_details: row.details,
      task_type: "Medication",
      time_slot_label: isAsNeeded(row) ? "PRN" : "Extra",
      fulfillment_form: form,
      data: {},
      patient: patientInfo,
    });
  }, [patientInfo]);

  useEffect(loadOrders, [loadOrders]);
  useEffect(loadTasks, [loadTasks]);
  useEffect(loadStock, [loadStock]);
  useEffect(() => {
    getRoster()
      .then((r) => {
        const map = {};
        listOf(r).forEach((n) => { map[n.nurse_id] = n.name; });
        setNurseNameById(map);
      })
      .catch(() => {});
  }, []);

  return (
    <Box>
      <MedicineList rows={orders} loading={ordersLoading} stock={stock} onGiveNow={giveNow} />
      {tasksLoading && <LinearProgress sx={{ mb: 1 }} />}
      <MarGrid
        tasks={tasks}
        date={date}
        onDateChange={setDate}
        onGive={setGiving}
        nurseNameById={nurseNameById}
      />
      <MedicationAdministrationSheet
        open={Boolean(giving)}
        task={giving}
        stock={giving ? stockFor(stock, giving.prescription_id) : undefined}
        performedById={currentNurseId}
        onClose={() => setGiving(null)}
        // The order book moves too: recording the first dose of an order is
        // what flips it out of "not started" in the list above.
        onRecorded={() => { loadTasks(); loadOrders(); loadStock(); }}
      />
    </Box>
  );
}
